import React from "react";
import { Routes, Route, Navigate, NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { isKpiAdmin } from "../../../utils/moduleAccess";
import KPIDashboard from "./KPIDashboard";
import KPIWorks from "./KPIWorks";
import KPIGroups from "./KPIGroups";
import KPICreateWork from "./KPICreateWork";
import KPICalendar from "./KPICalendar";
import KPIAssessments from "./KPIAssessments";
import KPIReports from "./KPIReports";
import KPISettings from "./KPISettings";
import KPIWorkDetails from "./KPIWorkDetails";
import KPIRecurringDetails from "./KPIRecurringDetails";
import KPIRecurringList from "./KPIRecurringList";
import KPIActivityLog from "./KPIActivityLog";
import KPILeaderboard from "./KPILeaderboard";

const KPI = ({ isDeveloper }) => {
  const location = useLocation();
  const authState = useSelector((state) => state.auth);

  const { myWorks, upcomingRecurring } = useSelector((state) => state.kpi);
  const worksCount = myWorks?.summary?.total !== undefined 
    ? myWorks.summary.total 
    : (Array.isArray(myWorks?.works) ? myWorks.works.length : null);
  const recurringCount = Array.isArray(upcomingRecurring) ? upcomingRecurring.length : undefined;

  const canAccessAdminModules = isKpiAdmin(authState);

  const allTabs = [
    { name: "Dashboard", path: "dashboard" },
    { name: "Works", path: "works" },
    { name: "Create Work", path: "create" },
    { name: "Calendar", path: "calendar" },
    { name: "Assessments", path: "assessments", badge: "ADMIN", adminOnly: true },
    // { name: "Reports", path: "reports" },
    { name: "Settings", path: "settings", adminOnly: true },
  ];

  const tabs = allTabs.filter(tab => !tab.adminOnly || canAccessAdminModules);

  return (
    <div className="min-h-screen flex flex-col kpi-container bg-gray-50" data-kpi-scope="true">
      {/* Sub Header Navigation */}
      <div className="bg-white border-b border-slate-200/90 shadow-2xs">
        <div className="container mx-auto">
          <div className="flex items-center gap-5 sm:gap-8 h-10 overflow-x-auto max-w-full scrollbar-none whitespace-nowrap">
            {tabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={`/kpi/${tab.path}`}
                className={({ isActive }) => {
                  const isTabActive = isActive || location.pathname.includes(`/kpi/${tab.path}`);
                  return `flex items-center h-full gap-1.5 text-xs sm:text-sm font-medium border-b-2 transition-all shrink-0 !no-underline hover:!no-underline focus:!no-underline active:!no-underline ${
                    isTabActive
                      ? "border-slate-900 !text-slate-900 font-semibold"
                      : "border-transparent text-slate-500 hover:text-slate-900"
                  }`;
                }}
              >
                <span>{tab.name}</span>
                {tab.count !== undefined && (
                  <span className="text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">
                    {tab.count}
                  </span>
                )}
                {tab.badge && (
                  <span className="text-[9px] sm:text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 uppercase tracking-wider">
                    {tab.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 w-full">
        <div className="container mx-auto py-4 sm:py-6">
          <Routes>
            <Route path="dashboard" element={<KPIDashboard />} />
            <Route path="works" element={<KPIWorks />} />
            <Route path="groups" element={<KPIGroups />} />
            <Route path="works/:id" element={<KPIWorkDetails />} />
            <Route path="recurring" element={<KPIRecurringList />} />
            <Route path="recurring/:id" element={<KPIRecurringDetails />} />
            <Route path="create" element={<KPICreateWork />} />
            <Route path="calendar" element={<KPICalendar />} />
            <Route
              path="assessments"
              element={canAccessAdminModules ? <KPIAssessments /> : <Navigate to="/kpi/dashboard" replace />}
            />
            {/* <Route path="reports" element={<KPIReports />} /> */}
            <Route path="activity-log" element={<KPIActivityLog />} />
            <Route path="leaderboard" element={<KPILeaderboard />} />
            <Route
              path="settings"
              element={canAccessAdminModules ? <KPISettings /> : <Navigate to="/kpi/dashboard" replace />}
            />
            <Route path="/" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default KPI;
